import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { AlertTriangle, Download, ExternalLink, Loader2 } from "lucide-react";

/**
 * Right-hand-side preview panel for a generated document.
 * - .docx → mammoth HTML rendered directly into the panel
 * - .pdf  → embedded iframe pointing at the same preview endpoint
 *
 * Consultant-only — falls back to Download button if preview fails.
 */
export default function DocumentPreviewSheet({ doc, open, onOpenChange }) {
  const [state, setState] = useState({ loading: true, html: null, warnings: [], err: null });

  // Sanitise once per html payload — DOMPurify is O(n) on doc length and
  // shouldn't rerun on unrelated re-renders (parent state changes, etc.).
  const safeHtml = useMemo(
    () => (state.html ? DOMPurify.sanitize(state.html, { USE_PROFILES: { html: true } }) : ""),
    [state.html],
  );

  useEffect(() => {
    if (!open || !doc) return;
    setState({ loading: true, html: null, warnings: [], err: null });
    if (doc.filename?.toLowerCase().endsWith(".pdf")) {
      setState({ loading: false, html: null, warnings: [], err: null, kind: "pdf" });
      return;
    }
    api.get(`/documents/${doc.id}/preview`)
      .then((r) => setState({ loading: false, html: r.data.html, warnings: r.data.warnings || [], err: null, kind: "docx" }))
      .catch((e) => setState({ loading: false, html: null, warnings: [], err: e.response?.data?.detail || "Preview failed" }));
  }, [open, doc]);

  async function download() {
    if (!doc) return;
    const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = doc.filename; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-hidden flex flex-col p-0" data-testid="doc-preview-sheet">
        <SheetHeader className="p-4 border-b border-slate-200 bg-slate-50">
          <SheetTitle className="text-[#0D1B2A] text-base truncate">
            {doc?.template_name || doc?.filename || "Preview"}
          </SheetTitle>
          <p className="text-[11px] text-slate-500 flex items-center gap-2">
            <span>{doc?.version}</span>
            <span>·</span>
            <span>{doc && (doc.size_bytes / 1024).toFixed(0)} KB</span>
            {state.warnings?.length > 0 && (
              <span className="ml-auto text-amber-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />{state.warnings.length} formatting warning{state.warnings.length === 1 ? "" : "s"}
              </span>
            )}
          </p>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto bg-white" data-testid="preview-body">
          {state.loading && (
            <div className="p-8 flex flex-col items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin text-[#1A7F6E]" />
              Rendering preview…
            </div>
          )}
          {state.err && (
            <div className="p-8 text-center text-sm text-rose-700">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
              {state.err}
            </div>
          )}
          {!state.loading && state.kind === "pdf" && doc && (
            <iframe
              key={doc.id}
              title={doc.filename}
              src={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/preview`}
              className="w-full h-full min-h-[calc(100vh-140px)] border-0"
              data-testid="preview-pdf-frame"
            />
          )}
          {!state.loading && state.kind === "docx" && state.html !== null && (
            <div
              className="prose prose-slate max-w-none p-6 text-sm leading-relaxed"
              // Server-rendered mammoth HTML is sanitised with DOMPurify to
              // strip <script>, event handlers, and any XSS vectors before
              // React commits the innerHTML. Memoised so it doesn't rerun on
              // unrelated re-renders.
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: safeHtml }}
              data-testid="preview-html"
            />
          )}
        </div>
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={download} data-testid="preview-download-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" />Download
          </Button>
          <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} data-testid="preview-close-btn">
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />Close
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
